import { Store } from '@ngrx/store';
import moment from 'moment';
import { ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';

import { LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';
import { getFeatureFlagObservable } from 'app/core/state';
import { prettyPlayerId } from 'app/core/utilities/user-util';
import { Timeout } from 'app/types';
import { Loggable } from 'app/utils/logging/loggable';
import { closeRtcPeer } from 'app/utils/media/rtc';
import {
  SubscribeAnswerMessage,
  OutgoingMessageType,
  ReconfigureMessage,
  SubscribeCandidateMessage,
  PeerCandidate,
  SubscribeMessage,
  UnsubscribeMessage,
  ParticipantState,
  BaseUser,
  PublishingStartedMessage,
  IncomingMessageType,
  IncomingMessage,
  OutgoingMessage,
} from 'messages/websocket.messages';
import { SubscribeSubstreamMessage } from 'messages/websocket.messages';

import PreciateEventHelper from '../preciate-stream/preciate-event-helper';
import PreciateEventType from '../preciate-stream/preciate-event-type';
import { RtcStatsStreamService, StatsCounter } from '../services/rtc-stats-stream';
import { SceneService } from '../services/scene.service';
import { SubscriptionReason, SubscriptionType } from '../services/subscription/subscription.types';
import { VideoRoomService } from '../services/video-room.service';
import { WebsocketService } from '../services/websocket.service';
import UnsubscribeListener from '../social-pixi-canvas/player';
import { ConnectionStatus } from '../social-pixi-canvas/player-components/player.state';
import { actions, socialAppActions, socialAppSelectors } from '../state/app';
import { SubscriptionState } from '../types/webrtc';

import ParticipantRtcStats from './participant-rtc-stats';

export const SUBSCRIBING_TIMEOUT_MS = 1000 * 30;
export const UNSUBSCRIBING_TIMEOUT_MS = 1000 * 10;
const INVALID_PUBLISH_TIME = 0;

export interface MediaStreamWrapper {
  id: string;
  stream?: MediaStream;
}

interface Peer {
  id: number;
  connection: RTCPeerConnection;
}

export class RoomParticipant extends Loggable {
  public initialMicEnabled: boolean;
  public initialVideoEnabled: boolean;
  private _peer?: Peer;
  private closePeer?: Peer;
  private _temporalLayer = 2;
  private _spatialLayer = 2;

  public readonly unsubscribeTime = 5000;
  public startX: number;
  public startY: number;
  public unsubscribeListener?: UnsubscribeListener;
  public unsubscribeTimeout: Timeout | null = null;
  public user: BaseUser;
  public userId: string;

  public audioElement?: HTMLAudioElement;
  public videoElement?: HTMLVideoElement;
  private _audioNeeded = false;
  private _videoNeeded = false;

  private lowBandwidthMode = false;
  private participantAudioTrackEnabled = true;
  private participantVideoTrackEnabled = true;
  private peerConnectionTime?: number; // Track peer connection start time (unix milliseconds)
  private publishPeerTime = INVALID_PUBLISH_TIME; // The time when the peer published.
  private retryTimeout: Timeout | null = null;
  private subscribingTimeout?: Timeout;
  private unsubscribingTimeout?: Timeout;

  private rtcStats: ParticipantRtcStats | null = null;
  private rtcVideoStatsInterval: Timeout | null = null;
  private websocketService: WebsocketService;

  private _subscriptionState: SubscriptionState = SubscriptionState.NOT_SUBSCRIBED;
  private destroyed$ = new ReplaySubject<boolean>(1);
  private _reason?: SubscriptionReason;

  private shouldReconfigure = false;

  public constructor(
    private videoRoomService: VideoRoomService,
    participantState: ParticipantState,
    private store: Store,
    private sceneService: SceneService,
    private rtcStatsStreamService: RtcStatsStreamService,
  ) {
    super();
    this.setDisplayName('RoomParticipant');

    const { camera, mic, publishPeerTime, user, x: startX, y: startY } = participantState;

    this.publishPeerTime = publishPeerTime;
    this.user = user;
    this.userId = user.userId;
    this.startX = startX;
    this.startY = startY;
    this.initialMicEnabled = mic;
    this.initialVideoEnabled = camera;
    this.participantAudioTrackEnabled = mic;
    this.participantVideoTrackEnabled = camera;
    this.audioElement = this.createAudio();

    this.websocketService = this.videoRoomService.websocketService;

    this.shouldReconfigure = false;

    this.info('Created participant', this.userContext);
    this.websocketService.message$.pipe(takeUntil(this.destroyed$)).subscribe(this.handleWebsocketMessage);

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.social_low_bandwidth_mode)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((lowBandwidthMode) => (this.lowBandwidthMode = lowBandwidthMode));

    this.store
      .select(socialAppSelectors.isPresentationActive)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isPresentationActive) => {
        if (isPresentationActive) {
          this.muteAudio();
        } else {
          this.unmuteAudio();
        }
      });
  }

  public subscribe(): void {
    if (this.publishPeerTime === INVALID_PUBLISH_TIME) {
      this.info('Participant has not published. Waiting for publish peer time', this.userContext);
      this.subscriptionState = SubscriptionState.WAITING_FOR_PEER;
      return;
    }
    if (this._peer) {
      this.warn('Already subscribed: ', this.userContext);
      return;
    }
    if (this.retryTimeout) {
      clearInterval(this.retryTimeout);
      this.retryTimeout = null;
    }
    const subId = this.userId;
    if (!subId) {
      this.info('Participant removed - not subscribing', this.userContext);
      return;
    }

    this.subscriptionState = SubscriptionState.SUBSCRIBING;

    const peerId = Date.now();

    const config = this.videoRoomService.createRtcConfig();
    const conn = new RTCPeerConnection(config);

    this.peer = {
      connection: conn,
      id: peerId,
    };

    conn.onconnectionstatechange = () => {
      const { connectionState } = conn;
      this.info('Subscribe state change', { ...this.userContext, connectionState, subId, peerId });
      switch (connectionState) {
        case 'connected':
          this.subscriptionState = SubscriptionState.SUBSCRIBED;

          this.peerConnectionTime = Date.now();

          this.sendRtcStats(connectionState).catch((e) =>
            this.error('Error sending rtc stats for room participant', e),
          );
          this.startRtcStats();
          this.videoRoomService.startPeerConnectionStats('RoomParticipantService', 1000);
          if (this.shouldReconfigure) {
            this.shouldReconfigure = false;
            this.info('reconfigure: Subscription complete, calling reconfigure...', {
              ...this.userContext,
              subscriptionState: this.subscriptionState,
            });
            this.reconfigure();
          }
          break;

        case 'failed':
          this.error('Peer connection failed', { ...this.userContext, connectionState, subId, peerId });
          this.subscriptionState = SubscriptionState.FAILED;

          this.handleConnectionFailed();
          break;
        case 'disconnected':
          closeRtcPeer(conn);
          break;
        default:
          break;
      }
    };

    conn.oniceconnectionstatechange = (): void => {
      const { iceConnectionState } = conn;
      this.info('Subscribe ICE state change', { ...this.userContext, iceConnectionState, subId, peerId });
    };

    conn.onicegatheringstatechange = (): void => {
      const { iceGatheringState } = conn;
      this.info('Subscribe ICE gathering state change', {
        ...this.userContext,
        iceGatheringState,
        subId,
        peerId,
      });
    };

    conn.onicecandidateerror = (e) => this.trace('Candidate error', subId, e);
    conn.ontrack = (event) => this.onTrack(event);
    conn.onicecandidate = (event): void => {
      this.sendCandidate(event, peerId);
    };

    const v = this.videoNeeded;
    const a = !!this.audioNeeded;
    this.info('Subscribe', { ...this.userContext, subId, videoNeeded: v, audioNeeded: a, peerId });

    const msg: SubscribeMessage = { t: OutgoingMessageType.SUB, subId, v, a, pt: peerId };
    this.websocketService.sendMessage(msg);
    this.videoRoomService.subscriptions.set(subId, true);
    this.info('Subscribe List Size: -- ', this.videoRoomService.subscriptions.size);
  }

  public setReason(reason?: SubscriptionReason): void {
    this._reason = reason;
    this.store.dispatch(
      actions.CHANGE_SUBSCRIPTION_STATUS({
        status: {
          audioNeeded: this.audioNeeded,
          id: this.user.userId,
          name: prettyPlayerId(this.user),
          reason,
          state: this._subscriptionState,
          videoNeeded: this.videoNeeded,
          type: SubscriptionType.PARTICIPANT,
          peerTime: this.publishPeerTime ?? 0,
        },
      }),
    );
  }

  public scheduleUnsubscription(): void {
    if (!this.unsubscribeTimeout && this._peer) {
      this.unsubscribeTimeout = setTimeout(() => this.unsubscribe(), this.unsubscribeTime);
    }
  }

  private cancelUnSubscriptionTimeout(): void {
    if (this.unsubscribeTimeout) {
      clearTimeout(this.unsubscribeTimeout);
      this.unsubscribeTimeout = null;
      this.info('Un-subscription cancelled, reconfigure/subscribe requested');
    }
  }

  public quickUnSubscription(): void {
    this.unsubscribe();
  }

  private handleWebsocketMessage = (msg: IncomingMessage): void => {
    const { t: type } = msg;
    switch (type) {
      case IncomingMessageType.CLOSE_PEER: {
        const { pt: peerId, userId } = msg;
        if (userId !== this.userId) {
          return;
        }
        this.closePeerConnection(peerId);
        break;
      }

      case IncomingMessageType.PUBLISHING_HANGUP: {
        const { publishPeerTime, userId } = msg;
        if (userId !== this.userId) {
          return;
        }
        this.publishPeerTime = 0;
        this.info('Peer hangup, unsubscribing', { ...this.userContext, publishPeerTime });
        this.unsubscribe();
        break;
      }

      case IncomingMessageType.SERVER_ERROR: {
        const { errorText, relatedMessage } = msg;

        this.handleServerError(errorText, relatedMessage as OutgoingMessage);
        break;
      }

      case IncomingMessageType.SUBCAN: {
        const { d, pt: peerTime, subId } = msg;
        if (subId !== this.userId) {
          return;
        }
        const candidate = new RTCIceCandidate(d);
        this.addCandidate(candidate, peerTime).catch((e) =>
          this.error('Adding candidate failed', { ...this.userContext, candidate, e }),
        );
        return;
      }

      case IncomingMessageType.SUBOFF: {
        const { d, pt: peerTime, subId } = msg;
        if (subId !== this.userId) {
          return;
        }
        this.addOffer(d, peerTime).catch((e) => {
          this.warn('Error adding offer', { ...this.userContext, e, peerTime });
        });
        return;
      }
    }
  };

  private handleServerError(errorText: string, relatedMessage: OutgoingMessage): void {
    const { t } = relatedMessage;
    switch (t) {
      case OutgoingMessageType.SUB: {
        const { subId } = relatedMessage;
        // Fail the subscription if the server returns an error.
        if (subId === this.userId) {
          this.info('Handled Server error, subscription failed', { ...this.userContext, errorText, subId });
          this.subscriptionState = SubscriptionState.FAILED;
        }
        return;
      }

      case OutgoingMessageType.UNSUB: {
        const { pt: peerId, subId } = relatedMessage;
        // Close the peer if the server returns an error on unsubscribe
        if (subId === this.userId) {
          this.info('Handled Server error, unsubscription failed', { ...this.userContext, errorText, subId });

          this.closePeerConnection(peerId);
        }
      }
    }
  }

  /**
   * Unsubscribe from the peer connection.
   *
   * This will set the peer connection aside to be closed when the server responds with a close peer message.
   *
   * @returns true if the unsubscribe was successful, false otherwise
   */
  private unsubscribe(): boolean {
    this.cancelUnSubscriptionTimeout();
    this.cleanVideoElement();
    if (!this._peer || this.subscriptionState === SubscriptionState.UNSUBSCRIBING) {
      return false;
    }
    this.subscriptionState = SubscriptionState.UNSUBSCRIBING;
    const { audioNeeded, peer, userId, videoNeeded } = this;
    this.info('Unsubscribe', {
      ...this.userContext,
      videoNeeded,
      audioNeeded,
      peerTime: peer?.id,
    });

    if (this.closePeer) {
      this.info('Closing peer already set', { ...this.userContext, closePeerId: this.closePeer.id });
    }

    this.closePeer = this.peer;
    this.peer = undefined;
    this.videoRoomService.removeSubscription(this.userId);

    if (peer) {
      const msg: UnsubscribeMessage = {
        t: OutgoingMessageType.UNSUB,
        subId: userId,
        pt: peer.id,
      };
      this.websocketService.sendMessage(msg);
    }

    // Remove RTC Stats Interval
    this.destroyRtcStats();
    this.info('Unsubscribe List Size: -- ', this.videoRoomService.subscriptions.size);
    return true;
  }

  public get shouldBeSubscribed(): boolean {
    return this.audioNeeded || this.videoNeeded;
  }

  public shouldResubscribe(): boolean {
    if (!this._peer) {
      return false;
    }

    if (this._peer.connection.connectionState === 'new') {
      return false;
    }

    return true;
  }

  /**
   * Reconfigure the subscription based on current media need.
   * This will add and remove audio and video as necessary based on client state..
   * @returns true if reconfigure was sent
   */
  public reconfigure(): boolean {
    const { audioNeeded, peer, userId, videoNeeded } = this;
    if (this._subscriptionState === SubscriptionState.SUBSCRIBING) {
      this.shouldReconfigure = true;
      this.info('reconfigure: skipping reconfigure, still subscribing. Waiting subscription...', {
        ...this.userContext,
        subscriptionState: this.subscriptionState,
      });
      return false;
    } else if (this._subscriptionState !== SubscriptionState.SUBSCRIBED || !peer) {
      this.info('reconfigure: skipping reconfigure, not subscribed', {
        ...this.userContext,
        subscriptionState: this.subscriptionState,
      });
      return false;
    }
    this.cancelUnSubscriptionTimeout();
    this.info('Reconfigure subscription', {
      ...this.userContext,
      videoNeeded,
      audioNeeded,
      peerId: peer.id,
    });
    const msg: ReconfigureMessage = {
      t: OutgoingMessageType.SUBCFG,
      subId: userId,
      v: videoNeeded,
      a: !!audioNeeded,
      pt: peer.id,
    };
    this.websocketService.sendMessage(msg);
    return true;
  }

  private async addOffer(offer: RTCSessionDescriptionInit, offerPeerId: number): Promise<void> {
    if (!this.userId) {
      this.error('addOffer: Could not add offer, userId is not set');
      return;
    }
    if (!this.peer) {
      this.warn('addOffer: Already unsubscribed, discarding offer.', this.userContext);
      return;
    }

    if (this.peer.id !== offerPeerId) {
      this.warn('addOffer: Offer peer id mismatch. Discarding offer as not from this subscription', {
        ...this.userContext,
        peerId: this.peer.id,
        offerPeerId,
      });
      return;
    }
    await this.peer.connection.setRemoteDescription(offer);

    // If unsubscribe is called during the set of remote description, peer can be undefined
    if (!this.peer) {
      return;
    }
    this.peer.connection.getTransceivers().forEach((t) => (t.direction = 'recvonly'));
    const answer = await this.peer.connection.createAnswer();
    const { sdp, type } = answer;
    if (!type || !sdp) {
      this.error('addOffer: Got unexpected undefined value in answer', answer);
      return;
    }

    // If unsubscribe is called during the answer creation, peer can be undefined
    if (!this._peer) {
      return;
    }
    await this._peer.connection.setLocalDescription(answer);

    const msg: SubscribeAnswerMessage = {
      t: OutgoingMessageType.SUBANS,
      subId: this.userId,
      answer: { type, sdp },
      pt: offerPeerId,
    };

    this.websocketService.sendMessage(msg);
  }

  /**
   * Add a candidate to this peer connection
   * @param candidate A new ICE Candidate
   * @param candidatePeerId The peer time of the current peer connection
   * @returns
   */
  private async addCandidate(candidate: RTCIceCandidate, candidatePeerId: number): Promise<void> {
    if (!this.peer) {
      this.warn('Already unsubscribed for candidate', this.userContext);
      return;
    }
    if (this.peer.id !== candidatePeerId) {
      this.warn('Candidate peer time mismatch', { ...this.userContext, peerId: this.peer.id, candidatePeerId });
      return;
    }
    await this.peer.connection.addIceCandidate(candidate);
  }

  private createVideo(): HTMLVideoElement {
    const videoElement = document.createElement('video');
    videoElement.muted = true;
    videoElement.onplaying = () => {
      videoElement.onplaying = null;
      if (this.videoNeeded && !this._peer) {
        this.subscribe();
      }
    };
    videoElement.oncanplay = () => {
      this.sceneService.sceneManager?.setPlayerConnectionStatus(this.userId, ConnectionStatus.CONNECTED);
      videoElement.oncanplay = null;
      videoElement.play().catch((e) => this.error('Error playing video', e));
      this.videoRoomService.didAddParticipantVideo.next(this);
    };
    this.videoElement = videoElement;
    return videoElement;
  }

  private cleanVideoElement(): void {
    if (this.videoElement) {
      this.videoElement.pause();
      this.videoElement.srcObject = null;
    }
    this.videoElement = undefined;
    this.unsubscribeListener?.unsubscribed();
  }

  private createAudio(): HTMLAudioElement {
    const audio = document.createElement('audio');
    audio.oncanplay = () => {
      audio.oncanplay = null;
      audio.play().catch((e) => this.error('Error playing audio', e));
    };
    audio.volume = 0;
    this.audioElement = audio;
    return audio;
  }

  public muteAudio(): void {
    if (this.audioElement) {
      this.audioElement.muted = true;
    }
  }

  public unmuteAudio(): void {
    if (this.audioElement) {
      this.audioElement.muted = false;
    }
  }

  public participantToggleAudio(value: boolean): void {
    this.participantAudioTrackEnabled = value;
    this.store.dispatch(socialAppActions.PARTICIPANT_AUDIO_MUTE_CHANGE({ userId: this.userId, muted: !value }));
  }

  public videoOnEvent(): void {
    this.participantVideoTrackEnabled = true;
    this.store.dispatch(socialAppActions.PARTICIPANT_VIDEO_MUTE_CHANGE({ userId: this.userId, muted: false }));
    this.videoRoomService.didSubscribeParticipant.next(
      new PreciateEventHelper(this.userId ?? '', this.userId ?? '', PreciateEventType.SUBSCRIBED),
    );
  }

  public videoOffEvent(): void {
    this.participantVideoTrackEnabled = false;
    this.store.dispatch(socialAppActions.PARTICIPANT_VIDEO_MUTE_CHANGE({ userId: this.userId, muted: true }));
    this.videoRoomService.didUnsubscribeParticipant.next(
      new PreciateEventHelper(this.userId ?? '', this.userId ?? '', PreciateEventType.UNSUBSCRIBE),
    );
  }

  private retrySubscription(): void {
    this.subscriptionState = SubscriptionState.RESUBSCRIBE;
    this.retryTimeout = setTimeout(() => {
      this.retryTimeout = null;
      this.unsubscribe();
      this.subscribe();
    }, 1500);
  }

  private onTrack(event: RTCTrackEvent): void {
    if (event.track.kind === 'video') {
      const video = this.videoElement || this.createVideo();
      if (!this.participantVideoTrackEnabled) {
        this.sceneService.sceneManager?.setPlayerConnectionStatus(this.userId, ConnectionStatus.CONNECTED);
      }
      if (!video.oncanplay) {
        video.oncanplay = () => {
          this.sceneService.sceneManager?.setPlayerConnectionStatus(this.userId, ConnectionStatus.CONNECTED);
          video.oncanplay = null;
          video.play().catch((e) => this.error('Error playing video', e));
        };
      }
      video.pause();
      video.srcObject = event.streams[0];
      // Produce a locally derived UUID here. Both the stream and track IDs coming from the video server seem to not be
      // unique.
      this.videoRoomService.participantVideoStreamChanged(this.userId, { id: uuidv4(), stream: event.streams[0] });
    } else if (event.track.kind === 'audio') {
      const audio = this.audioElement;
      if (!audio) {
        return this.error('onTrack executed without audioElement');
      }
      if (!audio.oncanplay) {
        audio.oncanplay = () => {
          audio.oncanplay = null;
          audio.play().catch((e) => this.error('Error playing audio', e));
        };
      }
      audio.srcObject = event.streams[0];
      // Produce a locally derived UUID here. Both the stream and track IDs coming from the video server seem to not be
      // unique.
      this.videoRoomService.participantAudioStreamChanged(this.userId, { id: uuidv4(), stream: event.streams[0] });
      this.videoRoomService.didAddParticipantAudio.next(this);
    }
  }

  private sendCandidate(event: RTCPeerConnectionIceEvent, peerTime: number): void {
    const { candidate } = event;
    if (!candidate) {
      this.info('ICE Candidate was null, done');
      return;
    }

    if (!this.userId) {
      this.error('sendCandidate: userId is not set when subscribing candidate');
      return;
    }
    if (!candidate.sdpMid) {
      this.error('Candidate sdpMid is unexpectedly null', candidate);
      return;
    }

    this.info('ICE Candidate received', candidate.toJSON());
    const msg: SubscribeCandidateMessage = {
      t: OutgoingMessageType.SUBCAN,
      subId: this.userId,
      d: candidate as PeerCandidate,
      pt: peerTime,
    };
    this.websocketService.sendMessage(msg);
  }

  private closePeerConnection(peerId?: number): void {
    if (!this.closePeer) {
      this.warn('closePeerConnection: No peer to close ', { ...this.userContext, peerId });
      return;
    }

    if (peerId !== undefined && this.closePeer.id !== peerId) {
      this.info('closePeerConnection: Peer id mismatch. Skipping peer close', {
        ...this.userContext,
        closePeerId: this.closePeer?.id,
        peerId,
      });
      return;
    }

    this.subscriptionState = SubscriptionState.UNSUBSCRIBED;

    this.info('closePeerConnection: Close peer', { ...this.userContext, peerId });
    closeRtcPeer(this.closePeer?.connection);
    this.closePeer = undefined;
    return;
  }

  public destroy(): void {
    this.info('Destroying participant', this.userContext);
    this.destroyRtcStats();

    this.videoElement?.pause();
    if (this.videoElement) {
      this.videoElement.srcObject = null;
      this.videoElement.oncanplay = null;
      this.videoElement.onplaying = null;
    }
    this.videoElement = undefined;
    this.audioElement = undefined;

    closeRtcPeer(this._peer?.connection);
    this.peer = undefined;

    this.destroyed$.next(true);
    this.destroyed$.complete();

    this.store.dispatch(socialAppActions.REMOVE_SUBSCRIPTION({ subscriptionId: this.user.userId }));
    this.videoRoomService.participantVideoStreamChanged(this.userId, undefined);
    this.videoRoomService.participantAudioStreamChanged(this.userId, undefined);
    this.videoRoomService.stopPeerConnectionStats();

    if (this.subscribingTimeout) {
      clearTimeout(this.subscribingTimeout);
    }
    this.subscribingTimeout = undefined;

    if (this.unsubscribeTimeout) {
      clearTimeout(this.unsubscribeTimeout);
    }
    this.unsubscribeTimeout = null;

    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
    }
    this.retryTimeout = null;

    if (this.userId) {
      this.videoRoomService.subscriptions.delete(this.userId);
    }
  }

  public publishingStarted({ publishPeerTime }: PublishingStartedMessage): void {
    this.info('Publishing started', { ...this.userContext, subscriptionState: this._subscriptionState });
    if (this._subscriptionState === SubscriptionState.SUBSCRIBING) {
      return;
    }

    if (this.publishPeerTime !== publishPeerTime) {
      this.publishPeerTime = publishPeerTime;

      if (this.shouldBeSubscribed) {
        this.info('Resubscribing to user due to new publish time', this.userContext);
        if (this._peer) {
          this.unsubscribe();
        }
        this.subscribe();
      }
    }
  }

  public get videoNeeded(): boolean {
    return this._videoNeeded;
  }

  private set videoNeeded(value: boolean) {
    // Skip video subscriptions if this client is in low bandwidth mode.
    this._videoNeeded = this.lowBandwidthMode ? false : value;
  }

  public get audioNeeded(): boolean {
    return this._audioNeeded;
  }

  private set audioNeeded(value: boolean) {
    this._audioNeeded = value;
  }

  public setMediaNeeded({ audio, video }: { video: boolean; audio: boolean }): void {
    this.videoNeeded = video;
    this.audioNeeded = audio;

    this.store.dispatch(
      socialAppActions.CHANGE_SUBSCRIPTION_STATUS({
        status: {
          audioNeeded: this.audioNeeded,
          id: this.user.userId,
          name: prettyPlayerId(this.user),
          reason: this._reason,
          state: this._subscriptionState,
          type: SubscriptionType.PARTICIPANT,
          videoNeeded: this.videoNeeded,
          peerTime: this.publishPeerTime ?? 0,
        },
      }),
    );
  }

  private async sendRtcStats(state: string): Promise<void> {
    if (this._peer?.connection.connectionState === 'closed') {
      return;
    }
    const stats = await this._peer?.connection.getStats();
    if (stats) {
      const rtcReports: RTCStatsReport[] = [];
      stats.forEach((report) => rtcReports.push(report));
      this.info('RTC Stats ', { ...this.userContext, rtcReports, state });
    } else {
      this.info('No RTC stats', { ...this.userContext }, state);
    }
  }
  /**
   * RTC Stats
   */
  private startRtcStats(): void {
    this.info('RTC Stats START for user:', this.userContext);
    const rtcStats = (this.rtcStats = new ParticipantRtcStats(this.userId, this.sceneService));

    if (this.rtcVideoStatsInterval) {
      clearInterval(this.rtcVideoStatsInterval);
    }
    this.rtcVideoStatsInterval = setInterval(async () => {
      // Validate that user exists
      const user = this.user;
      if (!user || !this._peer) {
        this.destroyRtcStats();
        return;
      }
      // Validate if peer and videoTrack is still available
      let isVideoStuck = false;
      let isAudioStuck = false;
      let stats: RTCStatsReport | null = null;

      if (!this._peer) {
        return;
      }

      // If necessary check if video is stuck
      if (this.videoNeeded && this.participantVideoTrackEnabled) {
        if (this._peer?.connection.connectionState === 'closed') {
          return;
        }

        await this._peer.connection
          .getStats()
          .then((retStats) => {
            stats = retStats;
            isVideoStuck = rtcStats.isVideoStuck(retStats, this.userId, user.firstName);
          })
          .catch((e) => {
            this.error('Error getting stats', e);
          });
      }

      // If necessary check if audio is stuck
      if (this.audioNeeded && this.participantAudioTrackEnabled) {
        if (!stats) {
          if (this._peer?.connection.connectionState === 'closed') {
            return;
          }
          await this._peer.connection
            .getStats()
            .then((retStats) => (stats = retStats))
            .catch((e) => {
              this.error('Error getting stats', e);
            });
        }
        if (stats) {
          isAudioStuck = rtcStats.isAudioStuck(stats, this.userId, user.firstName);
        } else {
          this.error('Error getting stats', { ...this.userContext, stats });
        }
      }
      if (!this.user) {
        this.destroyRtcStats();
        return;
      }
      if (isVideoStuck || isAudioStuck) {
        this.warn(`RoomParticipant Media is Stuck RESUBSCRIBE`, {
          ...this.userContext,
          isVideoStuck,
          isAudioStuck,
        });
        this.destroyRtcStats();
        this.handleConnectionFailed();
      }
    }, 1000);
  }

  private destroyRtcStats(): void {
    if (this.rtcVideoStatsInterval) {
      clearInterval(this.rtcVideoStatsInterval);
      this.rtcVideoStatsInterval = null;
    }
    if (this.rtcStats) {
      this.info('RTC Stats STOP for user', this.userContext);
      this.rtcStats.destroy();
      this.rtcStats = null;
    }
  }

  private handleConnectionFailed(): void {
    this.rtcStatsStreamService.incrementCounter(StatsCounter.PARTICIPANT_SUBSCRIBER_CONNECTION_FAILED);

    const oneMinuteAgo = moment().subtract(1, 'minute');
    const connectionTime = this.peerConnectionTime;

    // If the connection fails in under a minute, or we never connected, advance the reconnection state machine to
    // potentially more reliable methods
    if (!connectionTime || connectionTime > oneMinuteAgo.valueOf()) {
      this.videoRoomService.advanceReconnectionState({ connectionTime });
      // Clear the current connection time to avoid advancing state twice
      this.peerConnectionTime = undefined;
    }

    this.sendRtcStats('failed').catch((e) => this.error('Error sending rtc stats', e));
    this.retrySubscription();
  }

  private get userContext(): Record<string, string> {
    return {
      name: this.user?.firstName ?? 'Unknown',
      userId: this.userId ?? 'Unknown',
    };
  }

  public get subscriptionState(): SubscriptionState {
    return this._subscriptionState;
  }

  private set subscriptionState(state: SubscriptionState) {
    this.info(`Subscription state ${this._subscriptionState} -> ${state}`, {
      ...this.userContext,
      peerId: this.peer?.id,
    });

    // On state change, clear a subscribing timeout if it exists
    if (this.subscribingTimeout) {
      clearTimeout(this.subscribingTimeout);
      this.subscribingTimeout = undefined;
    }

    // On state change, clear anu subscribing timeout if it exists
    if (this.unsubscribingTimeout) {
      clearTimeout(this.unsubscribingTimeout);
      this.unsubscribingTimeout = undefined;
    }

    // When transitioning to SUBSCRIBING, set a timeout to retry the subscription if it takes too long
    if (state === SubscriptionState.SUBSCRIBING) {
      this.subscribingTimeout = setTimeout(() => {
        this.warn('Subscription SUBSCRIBING state timed out', { ...this.userContext, peerId: this.peer?.id });
        this.retrySubscription();
      }, SUBSCRIBING_TIMEOUT_MS);
    }

    // When transitioning to UNSUBSCRIBING, set a timeout to close the peer if it takes too long
    if (state === SubscriptionState.UNSUBSCRIBING) {
      this.unsubscribingTimeout = setTimeout(() => {
        if (this.subscriptionState !== SubscriptionState.UNSUBSCRIBING) {
          return;
        }
        this.warn('Subscription UNSUBSCRIBING timed out', { ...this.userContext, peerId: this.peer?.id });
        this.closePeerConnection(this.closePeer?.id);
      }, UNSUBSCRIBING_TIMEOUT_MS);
    }

    this._subscriptionState = state;
    this.store.dispatch(
      socialAppActions.CHANGE_SUBSCRIPTION_STATUS({
        status: {
          audioNeeded: this.audioNeeded,
          id: this.user.userId,
          name: prettyPlayerId(this.user),
          reason: this._reason,
          state,
          type: SubscriptionType.PARTICIPANT,
          videoNeeded: this.videoNeeded,
          peerTime: this.publishPeerTime ?? 0,
        },
      }),
    );
  }

  public toString(): string {
    return `RoomParticipant[${prettyPlayerId(this.user)}]`;
  }

  public get connection(): RTCPeerConnection | undefined {
    return this._peer?.connection;
  }

  private get peer() {
    return this._peer;
  }

  private set peer(newPeer: Peer | undefined) {
    this._peer = newPeer;
    if (newPeer) {
      this.rtcStatsStreamService.addSubscriptionMonitoring(
        this.userId,
        SubscriptionType.PARTICIPANT,
        newPeer.connection,
      );
    } else {
      this.rtcStatsStreamService.removeSubscriptionMonitoring(this.userId);
    }
  }

  public selectTemporalLayer(layer: number) {
    this._temporalLayer = layer;
    if (!this.peer) {
      return;
    }
    const subscribeSubstream: SubscribeSubstreamMessage = {
      t: OutgoingMessageType.SUB_SUBSTREAM,
      subId: this.userId,
      substream: this._spatialLayer,
      temporal: this._temporalLayer,
      pt: this.peer.id,
      svc: true,
    };
    this.websocketService.sendMessage(subscribeSubstream);
  }

  public selectSpatialLayer(layer: number) {
    this._spatialLayer = layer;
    if (!this.peer) {
      return;
    }
    const subscribeSubstream: SubscribeSubstreamMessage = {
      t: OutgoingMessageType.SUB_SUBSTREAM,
      subId: this.userId,
      substream: this._spatialLayer,
      temporal: this._temporalLayer,
      pt: this.peer.id,
      svc: true,
    };
    this.websocketService.sendMessage(subscribeSubstream);
  }

  public selectTemporalAndSpatialLayer(temporalLayer: number, spatialLayer: number, svc: boolean) {
    if (temporalLayer === this._temporalLayer && spatialLayer === this._spatialLayer) {
      return;
    }
    this._temporalLayer = temporalLayer;
    this._spatialLayer = spatialLayer;
    if (!this.peer) {
      return;
    }

    const subscribeSubstream: SubscribeSubstreamMessage = {
      t: OutgoingMessageType.SUB_SUBSTREAM,
      subId: this.userId,
      substream: this._spatialLayer,
      temporal: this._temporalLayer,
      pt: this.peer.id,
      svc: svc,
    };

    this.websocketService.sendMessage(subscribeSubstream);
  }

  public get spatialLayer(): number {
    return this._spatialLayer;
  }

  public get temporalLayer(): number {
    return this._temporalLayer;
  }

  // #region Test Utilities
  public forceClosePeer(): void {
    if (!this._peer) {
      this.warn('No peer to close', { ...this.userContext });
      return;
    }
    this.subscriptionState = SubscriptionState.FAILED;
    closeRtcPeer(this._peer.connection);
  }

  public get closePeerValues() {
    return this.closePeer ?? { connection: undefined, id: undefined };
  }

  public testSetPeerConnection(peer: Peer): void {
    this.peer = peer;
  }
  // #endregion
}
